import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Buchstaben-Welle um den Mauszeiger.
 *
 * Ersatz für das Gewichts-Morphing der Vorlage: Echtes Morphing bräuchte einen
 * Variable Font, Gotham ist statisch. Stattdessen heben und vergrössern sich
 * die Buchstaben nahe am Zeiger — dieselbe Wirkung, ohne zweiten Schriftschnitt.
 *
 * Drei Punkte, an denen der Prototyp-Code nicht 1:1 übernommen wurde:
 *
 * 1. **Die Rechtecke werden zwischengespeichert.** Das Original ruft
 *    `getBoundingClientRect()` für jeden Buchstaben bei jeder Mausbewegung —
 *    bei 14 Buchstaben sind das 14 erzwungene Layouts pro Event. Hier werden
 *    sie beim Betreten gemessen und nur bei `resize` verworfen.
 * 2. **Kein `requestAnimationFrame`.** Der Browser fasst `pointermove` ohnehin
 *    auf einen Frame zusammen, und weil die Rechtecke zwischengespeichert sind,
 *    kostet ein Durchlauf nur ein `Math.hypot` je Buchstabe. Ein rAF-Umweg
 *    brächte hier nur eine zusätzliche Frame-Verzögerung.
 * 3. **Barrierefreiheit.** Der Text wird in Einzelbuchstaben zerlegt; manche
 *    Screenreader buchstabieren das dann. Deshalb bekommt die Überschrift ein
 *    `aria-label` mit dem ursprünglichen Text.
 *
 * Läuft nur bei Feinzeiger (Maus, Trackpad) und ohne Bewegungswunsch. Auf
 * Touch bleibt das Markup unangetastet.
 */
export class KineticText extends Component {
  /** Wirkradius um den Zeiger. */
  static RADIUS = 130;
  /** Maximale Anhebung und Vergrösserung am Zeiger. */
  static LIFT = 5;
  static SCALE = 0.16;

  #chars = [];
  #rects = null;
  #target = null;
  #pointer = null;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion() || !matchMedia('(pointer: fine)').matches) return;

    this.#target = this.firstElementChild;
    if (!this.#target) return;

    // Vor dem Zerlegen sichern — danach ist der Text auf Dutzende Spans verteilt.
    // Über eine Kopie, in der `<br>` zu einem Leerzeichen wird: `textContent`
    // ignoriert Zeilenumbrüche, aus „Big on<br>smooth." würde sonst
    // „Big onsmooth." und der Screenreader läse zwei Wörter als eines.
    const copy = this.#target.cloneNode(true);
    for (const br of copy.querySelectorAll('br')) br.replaceWith(' ');
    const label = copy.textContent.replace(/\s+/g, ' ').trim();

    this.#split(this.#target);
    this.#chars = Array.from(this.querySelectorAll('.kinetic-text__char'));
    if (this.#chars.length === 0) return;

    if (label) this.#target.setAttribute('aria-label', label);
    this.setAttribute('data-enhanced', '');

    this.#target.addEventListener('pointerenter', this.#onEnter, { passive: true });
    this.#target.addEventListener('pointermove', this.#onMove, { passive: true });
    this.#target.addEventListener('pointerleave', this.#onLeave, { passive: true });
    addEventListener('resize', this.#invalidate, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    removeEventListener('resize', this.#invalidate);
  }

  /** Textknoten in Buchstaben-Spans zerlegen, `<em>` und `<br>` bleiben erhalten. */
  #split(node) {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const fragment = document.createDocumentFragment();

        for (const character of child.textContent) {
          // Leerzeichen bleiben echte Textknoten, sonst bricht der Zeilenumbruch.
          if (character === ' ') {
            fragment.appendChild(document.createTextNode(' '));
            continue;
          }
          const span = document.createElement('span');
          span.className = 'kinetic-text__char';
          span.textContent = character;
          fragment.appendChild(span);
        }

        node.replaceChild(fragment, child);
      } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') {
        this.#split(child);
      }
    }
  }

  #invalidate = () => {
    this.#rects = null;
  };

  #measure() {
    this.#rects = this.#chars.map((char) => {
      const rect = char.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    });
  }

  #onEnter = () => {
    this.#measure();
  };

  #onMove = (event) => {
    this.#pointer = { x: event.clientX, y: event.clientY };
    this.#apply();
  };

  #onLeave = () => {
    for (const char of this.#chars) char.style.transform = '';
  };

  #apply() {
    if (!this.#pointer) return;
    if (!this.#rects) this.#measure();

    const { RADIUS, LIFT, SCALE } = KineticText;

    this.#chars.forEach((char, index) => {
      const centre = this.#rects[index];
      const distance = Math.hypot(this.#pointer.x - centre.x, this.#pointer.y - centre.y);
      const strength = Math.max(0, 1 - distance / RADIUS);

      char.style.transform =
        strength > 0.01 ? `translateY(${-LIFT * strength}px) scale(${1 + SCALE * strength})` : '';
    });
  }
}

if (!customElements.get('kinetic-text')) {
  customElements.define('kinetic-text', KineticText);
}
